import { memo } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { CATEGORY_LINKS, IMAGE_PROXY_URL } from '../../utils/constant';
import { getTimer } from '../../utils/timer';

export function fixtureCardKey(fixture: any, categoryName?: string): string {
  return [
    categoryName || fixture?.category || '',
    fixture?.teamA || '',
    fixture?.teamB || '',
    fixture?.matchDate || fixture?.matchTime || 'highlights',
  ].join('|');
}

function formatMatchClock(fixture: any): string {
  if (!fixture?.matchDate) return 'Highlights';
  const d = new Date(fixture.matchDate);
  if (Number.isNaN(d.getTime())) return 'TBD';
  return dayjs(d).format('hh:mm A');
}

const MatchRow = memo(function MatchRow({ fixture }: { fixture: any }) {
  const timerValue = fixture?.matchDate ? getTimer(fixture) : null;
  const isLive = timerValue === 'Live';
  const clock = formatMatchClock(fixture);
  const teamsLabel = fixture?.teamB
    ? `${fixture.teamA} vs ${fixture.teamB}`
    : `${fixture.teamA}`;

  let link = `/fixture/${fixture.teamA}-vs-${fixture.teamB}`;
  if (!fixture?.matchDate) {
    const home = fixture?.teamA ?? '';
    const away = fixture?.teamB ?? '';
    const league = fixture?.subCategoryName ?? fixture?.category ?? '';
    const query = encodeURIComponent(`${home} vs ${away} highlights ${league}`);
    link = `https://www.youtube.com/results?search_query=${query}`;
  }

  const rowInner = (
    <div className={`bs-match-row${isLive ? ' is-live' : ''}`}>
      <div className="bs-match-teams">
        {fixture.teamAImage ? (
          <img
            className="bs-match-logo"
            src={`${IMAGE_PROXY_URL}${fixture.teamAImage}`}
            alt=""
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
          />
        ) : null}
        <span className="bs-match-label">{teamsLabel}</span>
        {fixture.teamBImage ? (
          <img
            className="bs-match-logo"
            src={`${IMAGE_PROXY_URL}${fixture.teamBImage}`}
            alt=""
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </div>

      <div className="bs-match-center">
        {isLive ? (
          <span className="bs-live-badge">LIVE</span>
        ) : timerValue && timerValue !== 'N/A' ? (
          <span className="bs-match-timer">{timerValue}</span>
        ) : fixture?.matchDate ? (
          <span className="bs-match-timer">{clock}</span>
        ) : (
          <span className="bs-match-timer">Highlights</span>
        )}
      </div>

      <div className="bs-match-meta">
        <span className="bs-live-streams-btn">Live Streams</span>
      </div>
    </div>
  );

  if (fixture?.matchDate) {
    return (
      <li>
        <Link to={link} className="bs-match-link">
          {rowInner}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <a href={link} target="_blank" rel="noopener noreferrer" className="bs-match-link">
        {rowInner}
      </a>
    </li>
  );
});

/** @deprecated kept for any external imports; prefer MatchRow via CategorySection */
export const FixtureCard = memo(function FixtureCard({
  fixture,
}: {
  fixture: any;
  isDesktop?: boolean;
}) {
  return <MatchRow fixture={fixture} />;
});

export const CategoryCard = memo(function CategoryCard({
  category,
  categoryName,
}: {
  category: any;
  categoryName?: string;
  isDesktop?: boolean;
}) {
  if (!category?.games?.length) return null;

  const leagueName = category.subCategoryName || 'Streams';
  const leagueKey = leagueName;
  const moreLink =
    !categoryName && CATEGORY_LINKS[category?.subCategoryName?.toUpperCase()]
      ? `/streams/${CATEGORY_LINKS[category.subCategoryName.toUpperCase()]}`
      : null;

  return (
    <section className="bs-sport-section">
      <div className="bs-sport-header">
        <div className="bs-sport-heading-wrap">
          {category?.subCategoryImage ? (
            <img
              className="bs-sport-icon"
              src={`${IMAGE_PROXY_URL}${category.subCategoryImage}`}
              alt=""
              width={28}
              height={20}
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <h2 className="bs-sport-title">{leagueName} Streams</h2>
        </div>
        {moreLink ? (
          <Link to={moreLink} className="bs-sport-more">
            More
          </Link>
        ) : null}
      </div>
      <h3 className="bs-sport-subtitle">Upcoming {leagueName} Streams Links</h3>
      <ul className="bs-match-list">
        {category.games.map((fixture: any) => {
          const withCategory = {
            ...fixture,
            category: category.subCategoryName,
            subCategoryName: category.subCategoryName,
          };
          return (
            <MatchRow
              key={fixtureCardKey(withCategory, leagueKey)}
              fixture={withCategory}
            />
          );
        })}
      </ul>
    </section>
  );
});
